import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, CircleDashed, ExternalLink, KeyRound, MessageSquareReply, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { otpReleased, useOTPInfo } from '@/lib/otp';
import { limitLabel } from '@/lib/official-labels';

// As duas FUNÇÕES que um template pode ter no bZapper, explicadas onde o cliente
// decide: a alternativa de fora da janela e o código de verificação. Cada uma
// com uma frase do que faz, o estado atual e UMA ação clara (escolher, ou criar
// a partir de um modelo pronto). O que depende da Meta (a liberação da categoria
// Autenticação) aparece como lista de exigências com o estado de cada uma e o
// link direto para onde se resolve.

interface Tpl {
  id: string; name: string; language: string; category: string; status: string; variables: number;
  spec?: { buttons?: { type: string }[] };
}

/**
 * Leva o código até a Meta liberar Autenticação: Marketing com botão de copiar
 * código (o cliente copia só o código; a Meta só aceita o botão em Marketing) ou
 * Utilidade com uma variável que recebe o código no texto.
 */
function otpProvisional(x: Tpl): boolean {
  if (x.category === 'MARKETING') return x.variables <= 1 && !!x.spec?.buttons?.some((b) => b.type === 'copy_code');
  return x.category === 'UTILITY' && x.variables === 1;
}

/** Modelo pronto que abre o formulário de criação já preenchido. */
export interface TemplatePreset {
  name: string;
  category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  body?: string;
  examples?: string[];
  footer?: string;
  buttons?: { type: 'copy_code'; text: string; example?: string }[];
}


// Onde a empresa se verifica na Meta: é o único passo que não dá para fazer daqui
// (documentos da empresa vão direto para a Meta).
const VERIFY_URL = 'https://business.facebook.com/settings/security';

export function TemplateRoles({ templates, fallback, onCreate }: {
  templates: Tpl[]; fallback: Tpl | null; onCreate: (p: TemplatePreset) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const approved = templates.filter((x) => x.status === 'APPROVED');
  const approvedAuth = approved.filter((x) => x.category === 'AUTHENTICATION');
  // Até a Meta liberar Autenticação, o código sai pelo provisório de Utilidade.
  const approvedProvisional = approved.filter(otpProvisional);
  // A alternativa recebe o texto do envio no lugar de {{1}}: serve template com
  // até uma variável (zero também serve — vai o texto fixo dele).
  const fallbackCandidates = approved.filter((x) => x.category !== 'AUTHENTICATION' && x.variables <= 1);

  const { data: otp } = useOTPInfo();

  const setRole = useMutation({
    mutationFn: async ({ role, id }: { role: 'fallback' | 'otp'; id: string }) => {
      const r = await apiFetch('PUT', `/templates/${role}`, { template_id: id });
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? t('templates.roles.saveFailed'));
      return role;
    },
    onSuccess: (role) => {
      qc.invalidateQueries({ queryKey: ['templates', role] });
      toast.success(t('templates.roles.saved'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const el = otp?.eligibility;
  const otpUnlocked = otpReleased(el);
  const otpTpl = otp?.template ?? null;

  const picker = (role: 'fallback' | 'otp', options: Tpl[], current: Tpl | null) => (
    <select
      value={current?.id ?? ''}
      onChange={(e) => setRole.mutate({ role, id: e.target.value })}
      className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
      aria-label={t(`templates.roles.${role}.pick`)}
    >
      <option value="">{options.length ? t(`templates.roles.${role}.pick`) : t('templates.roles.noneApproved')}</option>
      {options.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.language}</option>)}
    </select>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2 *:min-w-0">
      {/* Alternativa de fora da janela */}
      <Card>
        <CardContent className="flex h-full flex-col gap-3 p-5">
          <div className="flex items-start gap-3">
            <MessageSquareReply className="mt-0.5 size-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold">{t('templates.roles.fallback.title')}</p>
                {fallback
                  ? <Badge variant="success">{fallback.name}</Badge>
                  : <Badge variant="warning">{t('templates.roles.notSet')}</Badge>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t('templates.roles.fallback.what')}</p>
            </div>
          </div>
          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
            {t('templates.roles.fallback.example')}
          </div>
          <div className="mt-auto flex flex-wrap items-center gap-2">
            {picker('fallback', fallbackCandidates, fallback)}
            <Button size="sm" variant="outline" onClick={() => onCreate({
              name: 'aviso_atendimento', category: 'UTILITY',
              body: t('templates.roles.fallback.presetBody'),
              examples: [t('templates.roles.fallback.presetExample')],
            })}>
              <Sparkles className="size-3.5" />{t('templates.roles.createPreset')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Código de verificação */}
      <Card>
        <CardContent className="flex h-full flex-col gap-3 p-5">
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 size-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold">{t('templates.roles.otp.title')}</p>
                {otpTpl
                  ? otpTpl.category === 'AUTHENTICATION'
                    ? <Badge variant="success">{otpTpl.name}</Badge>
                    : <Badge variant="warning">{t('templates.roles.otp.provisional.badge', { name: otpTpl.name })}</Badge>
                  : approvedAuth.length > 0
                    ? <Badge variant="secondary">{t('templates.roles.otp.auto', { name: approvedAuth[0].name })}</Badge>
                    : <Badge variant="warning">{t('templates.roles.notSet')}</Badge>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{t('templates.roles.otp.what')}</p>
            </div>
          </div>

          {/* Liberado: escolhe ou cria o de Autenticação (o provisório continua na
              lista para quem já usa um e vai trocar). */}
          {otpUnlocked && <div className="mt-auto flex flex-wrap items-center gap-2">
            {picker('otp', [...approvedAuth, ...approvedProvisional], otpTpl)}
            <Button size="sm" variant="outline" onClick={() => onCreate({ name: 'codigo_acesso', category: 'AUTHENTICATION' })}>
              <Sparkles className="size-3.5" />{t('templates.roles.otp.create')}
            </Button>
          </div>}

          {/* Travado pela Meta. Não oferecemos mais criar um provisório de Marketing ou
              Utilidade: a Meta recusa código fora de Autenticação (INCORRECT_CATEGORY,
              provado em 21/10/2026). Quem já tem um provisório aprovado continua
              podendo escolhê-lo. A lista de exigências fica embaixo. */}
          {el && !otpUnlocked && (
            <>
              <div className="flex flex-col gap-2 rounded-md border border-primary/30 bg-primary/5 p-3">
                <p className="text-sm font-medium">{t('templates.roles.otp.provisional.title')}</p>
                <p className="text-xs text-muted-foreground">{t('templates.roles.otp.provisional.what')}</p>
                {approvedProvisional.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {picker('otp', approvedProvisional, otpTpl)}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                <p className="text-sm font-medium">{t('templates.roles.otp.locked')}</p>
                <Requirement ok={el.verification_ok} label={t('templates.roles.otp.reqVerify')}
                  detail={[
                    t(`templates.roles.otp.verify.${el.business_verification || 'unknown'}`, {}),
                    el.owner_business_name ? t('templates.roles.otp.verifyOwner', { name: el.owner_business_name }) : '',
                  ].filter(Boolean).join(' ')}
                  action={!el.verification_ok ? (
                    <a href={VERIFY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline">
                      {t('templates.roles.otp.verifyAction')}<ExternalLink className="size-3" />
                    </a>
                  ) : null} />
                <Requirement ok={el.limit_ok} label={t('templates.roles.otp.reqLimit')}
                  detail={t('templates.roles.otp.limitNow', { now: limitLabel(t, el.messaging_limit), need: limitLabel(t, el.required_limit) })}
                  action={!el.limit_ok ? <span className="text-xs text-muted-foreground">{t('templates.roles.otp.limitHow')}</span> : null} />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Requirement({ ok, label, detail, action }: { ok: boolean; label: string; detail: string; action: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {ok
        ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        : <CircleDashed className="mt-0.5 size-4 shrink-0 text-amber-600" />}
      <div className="min-w-0">
        <p className={cn('font-medium', ok && 'text-muted-foreground')}>{label}</p>
        <p className="text-xs text-muted-foreground">{detail}</p>
        {action}
      </div>
    </div>
  );
}
